import {
    RiArrowDownLine,
    RiArrowUpLine,
    RiLinksLine,
} from "@remixicon/react";
import React, {
    ReactElement,
    useState,
} from "react";
import {
    AllowedSignInMethods,
    GetPortalSettingsDocument, Permission, PortalIndexProvider,
    PortalSettingsIcon,
    PortalSettingsLink,
    PortalSettingsLinkVisibility,
    useGetPortalSettingsQuery, useSavePortalSettingsMutation,
} from "../../../../__generated__/api";
import EditorV3 from "../../../../components/agent-v3/editor/Editor";
import CodeBlock from "../../../../components/code-block/CodeBlock";
import CreateToken from "../../../../components/create-token/CreateToken";

import RequestTypes from "../../../../components/pickers/request-types/RequestTypes";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";
import Select from "../../../../components/select/Select";
import SettingsContainer from "../../../../components/theme/settings";
import useArticleContext from "../../../../contexts/HelpContext";
import usePortal from "../../../../contexts/PortalContext";

import { useFormBuilder } from "../../../../components/dirty-tracker/FormTracker";
import Alert from "../../../../components/theme/alert/Alert";
import Badge from "../../../../components/theme/badge/Badge";
import Button from "../../../../components/theme/button/Button";
import { useDrawerForm } from "../../../../components/theme/drawer";
import Dropdown from "../../../../components/theme/dropdown/Dropdown";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import Icon from "../../../../components/theme/icon/Icon";
import createTableContainer from "../../../../components/theme/table";
import Toggle from "../../../../components/theme/toggle/Toggle";
import createId from "../../../../create-id";
import { Time } from "../../../../date";
import Styles from "../../Administrator.module.scss";
import { PORTAL_SETTINGS_ICON_IMAGES } from "../../../../components/navigation/Navigation";
import DeflectionSource from "../../../../components/pickers/deflection-sources/DeflectionSource";

const LOGIN_TYPES = [
    {
        label: "Google",
        value: AllowedSignInMethods.Google,
    },
    {
        label: "Slack",
        value: AllowedSignInMethods.Slack,
    },
    {
        label: "Single Sign On",
        value: AllowedSignInMethods.SingleSignOn,
    },
];

const LOGIN_TYPES_LIST = LOGIN_TYPES.toIdList("value");

const DEFAULT_SESSION_DURATION = 90 * Time.DAY;

interface PortalSettingsLinkWithId extends PortalSettingsLink {
    id: string;
    index: number;
}

const TableContainer = createTableContainer<PortalSettingsLinkWithId>();

const VISIBLITY_OPTIONS = Object.values(PortalSettingsLinkVisibility).map(visbility => ({
    label: visbility.toLowerCase().capitalize(),
    value: visbility,
}));

export function useCreateLinkView(onSave: ((link: PortalSettingsLinkWithId, id?: string) => void)) {
    const [edit, setEdit] = useState<PortalSettingsLinkWithId>();

    const { element, setOpen, formTracker } = useDrawerForm({
        title: "Create Link",
        size: "medium",
        onHide: () => {
            formTracker.reset();
        },
        onSave: async () => {
            onSave({
                index: formTracker.properties.index.value,
                id: formTracker.properties.id.value,
                text: formTracker.properties.text.value,
                url: formTracker.properties.url.value,
                icon: formTracker.properties.icon.value as PortalSettingsIcon ?? PortalSettingsIcon.Website,
                visibility: formTracker.properties.visibility.value as PortalSettingsLinkVisibility,
            }, formTracker.properties.id.value);
        },
        formTrackerBuilder: {
            handler: builder => builder
                .property("index", () => edit?.index ?? 0)
                .property("id", () => edit?.id ?? "")
                .property("text", () => edit?.text ?? "")
                .property("url", () => edit?.url ?? "")
                .property("icon", () => edit?.icon ?? PortalSettingsIcon.Website)
                .property("visibility", () => edit?.visibility ?? PortalSettingsLinkVisibility.Public),
            dependencies: [edit],
        },
        body: tracker => (
            <>
                <Dropdown>
                    <Dropdown.Toggle
                        variant="outline-blue"
                        size="small"
                    >
                        <Icon
                            size="large"
                            icon={PORTAL_SETTINGS_ICON_IMAGES[tracker.properties.icon.value as PortalSettingsIcon]}
                        />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {Object.values(PortalSettingsIcon).map(icon => (
                            <Dropdown.Item
                                key={icon}
                                onClick={() => tracker.properties.icon.setValue(icon)}
                                prefixIcon={<Icon icon={PORTAL_SETTINGS_ICON_IMAGES[icon]} />}
                            >
                                {icon}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
                <FormGroup
                    type="text"
                    label="Link Text"
                    required
                    size="small"
                    onChange={tracker.properties.text.setValueFromInput}
                    value={tracker.properties.text.value}
                />
                <FormGroup
                    type="url"
                    label="Link URL"
                    required
                    size="small"
                    onChange={tracker.properties.url.setValueFromInput}
                    value={tracker.properties.url.value}
                />

                <FormGroup
                    label="Link Visibility"
                    required
                    size="small"
                >
                    <Select
                        className="text-gray"
                        placeholder="Select Icon"
                        onChange={option => tracker.properties.visibility.setValue(option?.value as PortalSettingsLinkVisibility)}
                        options={VISIBLITY_OPTIONS}
                        value={VISIBLITY_OPTIONS.find(row => row.value === tracker.properties.visibility.value)}
                    />
                </FormGroup>

            </>
        ),
    });

    return {
        element,
        setOpen,
        setEdit: ((link: PortalSettingsLink, id: string, index: number) => {
            setEdit({
                ...link,
                id,
                index,
            });
            setOpen(true);
        }),
    };
}

export default function PortalSettings(): ReactElement {
    useArticleContext("urn:fq:article:8d394cb07a3a4b5395ebe1559171d705");
    const { entitlementIds, isAIEnabled } = usePortal();

    const { data, error, loading: isLoading } = useGetPortalSettingsQuery();
    const [save] = useSavePortalSettingsMutation();

    const { properties, tracker } = useFormBuilder(
        builder => builder
            .property("title", () => data?.portal?.title ?? "")
            .property("welcomeMessage", () => data?.portal?.welcomeMessage ?? "")
            .property("welcomeMessage", () => data?.portal?.welcomeMessage ?? "")
            .property("publicAnswerProviderId", () => data?.portal?.publicAnswerProviderId)
            .property("guestAnswerProviderId", () => data?.portal?.guestAnswerProviderId)
            .array("publicPermissions", () => data?.portal?.publicPermissions ?? [])
            .array("guestPermissions", () => data?.portal?.guestPermissions ?? [])
            .array("publicFormIds", () => data?.portal?.publicFormIds ?? [])
            .array("guestFormIds", () => data?.portal?.guestFormIds ?? [])
            .property("isSharedChannelGuest", () => data?.portal?.isSharedChannelGuest ?? false)
            .property("isSingleSignOnEnabled", () => data?.portal?.singleSignOn?.isEnabled ?? false)
            .property("ssoLoginRedirectUrl", () => data?.portal?.singleSignOn?.loginRedirectUrl)
            .property("ssoIdentityProviderMetadata", () => data?.portal?.singleSignOn?.identityProviderMetadata ?? undefined)
            .property("scimAdministratorGroupId", () => data?.portal?.userProvisioning?.administratorGroupId)
            .property("portalIndexProvider", () => data?.portal?.portalIndexProvider ?? PortalIndexProvider.None)
            .array("allowedSigninMethods", () => data?.portal?.signIn?.allowedSigninMethods ?? LOGIN_TYPES_LIST)
            .array("navigation", () => data?.portal?.navigation?.links?.map((link, index) => ({
                ...link,
                id: createId(),
                index,
            })) ?? [])
            .property("defaultSignInMethod", () => data?.portal?.signIn?.defaultSignInMethod)
            .property("sessionDuration", () => data?.portal?.signIn?.sessionDuration ?? DEFAULT_SESSION_DURATION),
        [data?.portal],
    );

    const { element } = tracker.useDirtyTracker({
        finishedSavingMessage: "Portal settings saved!",
        onSave: async (event, onDone) => {
            await save({
                refetchQueries: [
                    { query: GetPortalSettingsDocument },
                ],
                variables: {
                    portal: {
                        title: properties.title.value,
                        welcomeMessage: properties.welcomeMessage.value,
                        publicPermissions: properties.publicPermissions.value,
                        guestPermissions: properties.guestPermissions.value,
                        publicFormIds: properties.publicFormIds.value,
                        guestFormIds: properties.guestFormIds.value,
                        portalIndexProvider: properties.portalIndexProvider.value,
                        publicAnswerProviderId: properties.publicAnswerProviderId.value,
                        guestAnswerProviderId: properties.guestAnswerProviderId.value,
                        isSharedChannelGuest: properties.isSharedChannelGuest.value,
                        singleSignOn: {
                            isEnabled: properties.isSingleSignOnEnabled.value,
                            loginRedirectUrl: properties.ssoLoginRedirectUrl.value,
                            identityProviderMetadata: properties.ssoIdentityProviderMetadata.value,
                        },
                        userProvisioning: {
                            administratorGroupId: properties.scimAdministratorGroupId.value,
                        },
                        signIn: {
                            sessionDuration: properties.sessionDuration.value,
                            allowedSigninMethods: properties.allowedSigninMethods.value,
                            defaultSignInMethod: properties.defaultSignInMethod.value,
                        },
                        navigation: {
                            links: properties.navigation.value.map(link => ({
                                ...link,
                                id: undefined,
                                index: undefined,
                                __typename: undefined,
                            })),
                        },
                    },
                },
            });
            onDone();
        },
    });

    const createListModifier = (
        current: Permission[],
        onChange: (permissions: Permission[]) => void,
        ...permission: Permission[]
    ) => (value: boolean) => {
        let copy = Array.from(current ?? []);
        if (value) {
            copy = copy.concat(permission);
        } else {
            const set = new Set(permission);
            copy = copy.filter(item => !set.has(item));
        }
        onChange(copy.dedupe());
    };

    const createToggleModifier = (
        current: Permission[],
        onChange: (permissions: Permission[]) => void,
        ...permission: Permission[]
    ) => {
        const handler = createListModifier(current, onChange, ...permission);
        return (event: React.ChangeEvent<HTMLInputElement>) => handler(event.target.checked);
    };

    const allowedSigninMethodsSet = new Set(properties.allowedSigninMethods.value);

    const loginTypes = properties.isSingleSignOnEnabled.value
        ? LOGIN_TYPES
        : LOGIN_TYPES.filter(option => option.value !== AllowedSignInMethods.SingleSignOn);

    const hasIdentityProvider = data?.portal?.singleSignOn?.identityProviderMetadata
        && properties.ssoIdentityProviderMetadata.value;

    const { element: createLinkElement, setOpen, setEdit: setEditLink } = useCreateLinkView((link, id) => {
        const copy = properties.navigation.value.map(item => (item.id === id ? link : item) as PortalSettingsLinkWithId);
        if (!id) {
            copy.push({
                ...link,
                id: createId(),
            });
        }

        properties.navigation.setValue(copy);
    });

    return (
        <SettingsContainer saveElement={element} error={error}>
            <SettingsContainer.Section title="General Settings" helpText="General settings">

                <SettingsContainer.Setting
                    title="Title"
                    description="The title of the portal displayed to all users that visit the site."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <FormGroup
                        type="text"
                        placeholder="My Portal"
                        required
                        size="small"
                        onChange={properties.title.setValueFromInput}
                        value={properties.title.value}
                    />
                </SettingsContainer.Setting>

                <SettingsContainer.Setting
                    title="Welcome Message"
                    description="The first message that a user sees when visiting the portal."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <EditorV3
                        value={properties.welcomeMessage.value}
                        size="medium"
                        hasBorder
                        onChange={properties.welcomeMessage.setValue}
                        placeholder={`Welcome to ${properties.title.value ?? "Our site"}`}
                    />
                </SettingsContainer.Setting>

            </SettingsContainer.Section>

            <SettingsContainer.Section
                title="Guest Permissions"
                helpText={"Guests are users who have access to Slack but are not Agents "
                    + "or Administrators. By default, all users who have access to your "
                    + "applications such as Slack or Microsoft Teams are guests."}
            >

                <SettingsContainer.Setting
                    title="Read Articles"
                    description="If enabled, all published articles become available to any
                                guest that has access to Slack.
                                All accounts and guest accounts who have access to your Slack
                                workspace will have access to these articles"
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >

                    <Toggle
                        checked={properties.guestPermissions.value?.includes(Permission.ArticleReadPublic) ?? false}
                        onChange={createToggleModifier(
                            properties.guestPermissions.value,
                            properties.guestPermissions.setValue,
                            Permission.ArticleReadPublic,
                        )}
                    />
                    {properties.guestPermissions.value?.includes(Permission.ArticleReadPublic) && (
                        <Alert variant="danger" className="mt-1">
                            All published articles are public and visible to everyone who is able to login
                            to this Portal as a guest.
                        </Alert>
                    )}

                </SettingsContainer.Setting>

                <SettingsContainer.Setting
                    title="Deflection Provider"
                    description="Which provider should be used to automatically answer users questions."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isVisible={isAIEnabled && properties.guestPermissions.value?.includes(Permission.ArticleReadPublic)}
                    isLoading={isLoading}
                >

                    <DeflectionSource
                        placeholder="Portal"
                        onSelected={properties.guestAnswerProviderId.setValue}
                        value={properties.guestAnswerProviderId.value}

                    />

                </SettingsContainer.Setting>

                <SettingsContainer.Setting
                    title="Create Tickets"
                    description="If enabled guests will be able to create tickets as themselves."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <Toggle
                        checked={properties.guestPermissions.value?.includes(Permission.TicketCreateGuest) ?? false}
                        onChange={createToggleModifier(
                            properties.guestPermissions.value,
                            properties.guestPermissions.setValue,
                            Permission.TicketCreateGuest,
                            Permission.TicketsReadSelf,
                        )}
                    />

                </SettingsContainer.Setting>

                <SettingsContainer.Setting
                    title="Request Types"
                    description="Specify which request types guest users are able to create.
                            To order the request types, click and drag them inside this box."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isVisible={properties.guestPermissions.value?.includes(Permission.TicketCreateGuest)}
                    isLoading={isLoading}
                >

                    <RequestTypes
                        isMulti
                        placeholder="The request types that guests can create."
                        onSelected={properties.guestFormIds.setValue}
                        value={properties.guestFormIds.value}
                    />

                </SettingsContainer.Setting>

                <SettingsContainer.Setting
                    title="Slack Connect users are guests"
                    description=" If yes, users coming from indirect sources such as Slack Connect channels will
                            be considered guests and will be able to view articles and create tickets."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isVisible={properties.guestPermissions.value?.includes(Permission.TicketCreateGuest)}
                    isLoading={isLoading}
                >

                    <Toggle
                        checked={properties.isSharedChannelGuest.value}
                        onChange={properties.isSharedChannelGuest.setCheckedFromInput}
                    />
                </SettingsContainer.Setting>

            </SettingsContainer.Section>
            <SettingsContainer.Section
                title="Public Permissions"
                helpText="Public permissions control what unauthenticated people on the internet are able to view."
            >
                <SettingsContainer.Setting
                    title="All Articles are Public"
                    description={"If enabled, all published articles become public to the "
                        + "internet. Everyone in the world will be allowed to read all articles."}
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >

                    <Toggle
                        checked={properties.publicPermissions.value?.includes(Permission.ArticleReadPublic) ?? false}
                        onChange={createToggleModifier(
                            properties.publicPermissions.value,
                            properties.publicPermissions.setValue,
                            Permission.ArticleReadPublic,
                        )}
                    />

                    {properties.publicPermissions.value?.includes(Permission.ArticleReadPublic) && (
                        <Alert variant="danger" className="mt-1">
                            All published articles are public a nd visible to everyone on the internet.
                        </Alert>
                    )}

                </SettingsContainer.Setting>
                <SettingsContainer.Setting
                    title="Deflection Provider"
                    description="Choose the AI provider that will handle questions
                    submitted by anyone, including from the public internet."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isVisible={isAIEnabled && properties.publicPermissions.value?.includes(Permission.ArticleReadPublic)}
                    isLoading={isLoading}
                >

                    <DeflectionSource
                        placeholder="Portal"
                        onSelected={properties.publicAnswerProviderId.setValue}
                        value={properties.publicAnswerProviderId.value}
                        isClearable
                    />

                </SettingsContainer.Setting>

            </SettingsContainer.Section>

            <SettingsContainer.Section
                title="Navigation Links"
                helpText="Allow users and customers to navigate to sites directly from the portal navigation bar."
            >
                <SettingsContainer.Setting
                    title="Navigation Links"
                    description="Allow users and customers to navigate to sites directly from the portal navigation bar."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isHideTitle
                >
                    <TableContainer
                        data={properties.navigation.value as PortalSettingsLinkWithId[] ?? []}
                        isLoading={isLoading}
                        borderVariant="secondary"
                    >
                        <TableContainer.Empty>
                            <TableContainer.Empty.Default
                                text="Navigation linls allow users and customers to navigate to sites
                                directly from the portal navigation bar."
                                title="No Links Navigation Links"
                                icon={<RiLinksLine />}
                                onClick={() => {
                                    setOpen(true);
                                }}
                                buttonText="Create Link"
                            />
                        </TableContainer.Empty>
                        <TableContainer.TableControls>
                            <TableContainer.TableControls.Search
                                search={(query, item) => item.text?.toString().includes(query)
                                    || item.url?.toString().includes(query)}
                                autoFocus={false}
                            />
                            <TableContainer.TableControls.Seperator />

                            <TableContainer.TableControls.Create
                                text="Create Link"
                                onClick={() => {
                                    setOpen(true);
                                }}
                            />
                        </TableContainer.TableControls>
                        <TableContainer.Table>
                            <TableContainer.TableValue
                                columnKey="icon"
                                title="Icon"
                            >
                                {item => (
                                    <Icon
                                        icon={PORTAL_SETTINGS_ICON_IMAGES[item.icon ?? PortalSettingsIcon.Website]}
                                        variant="default"
                                    />
                                )}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="text"
                                title="Text"
                            >
                                {item => item.text}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="url"
                                title="Link"
                            >
                                {item => item.url}
                            </TableContainer.TableValue>

                            <TableContainer.TableValue
                                columnKey="url"
                                title="Link"
                            >
                                {item => <Badge variant="gray">{item.visibility?.toLowerCase().capitalize()}</Badge> }
                            </TableContainer.TableValue>

                            <TableContainer.Action
                                icon={<Icon.Edit />}
                                altText="Edit Link"
                                onClick={item => {
                                    setEditLink(item, item.id, item.index);
                                }}
                            />
                            <TableContainer.Action
                                icon={<Icon icon={<RiArrowUpLine />} />}
                                altText="Move Link Up"
                                isDisabled={item => item.index === 0}
                                onClick={item => {
                                    const copy = [...properties.navigation.value];
                                    copy.splice(item.index, 1);
                                    copy.splice(item.index - 1, 0, item);
                                    copy.forEach((link, index) => {
                                        // eslint-disable-next-line no-param-reassign
                                        link.index = index;
                                    });
                                    properties.navigation.setValue(copy);
                                }}
                            />

                            <TableContainer.Action
                                icon={<Icon icon={<RiArrowDownLine />} />}
                                altText="Move Link Down"
                                isDisabled={item => item.index === properties.navigation.value.length - 1}
                                onClick={item => {
                                    setEditLink(item, item.id, item.index);
                                }}
                            />

                        </TableContainer.Table>
                    </TableContainer>
                </SettingsContainer.Setting>

            </SettingsContainer.Section>

            <SettingsContainer.Section
                title="Single Sign On"
                helpText="Single sign on allows users to sign into the Foqal portal using your Identity provider such as
                            Okta, Azure, or One Login"
            >
                <SettingsContainer.Setting
                    title="Enabled"
                    description="If enabled, will allow users to login to Foqal using an SSO provider such as Okta or Azure"
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <Toggle
                        checked={properties.isSingleSignOnEnabled.value}
                        onChange={properties.isSingleSignOnEnabled.setCheckedFromInput}
                    />
                </SettingsContainer.Setting>

                <SettingsContainer.Setting
                    title="Sign In URL"
                    description="Which URL to redirect the user to when requesting authentication.
                    This is using the SSO Provider's Foqal login URL."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <FormGroup
                        type="url"
                        required
                        size="small"
                        onChange={properties.ssoLoginRedirectUrl.setValueFromInput}
                        value={properties.ssoLoginRedirectUrl.value ?? ""}
                    />
                </SettingsContainer.Setting>

                <SettingsContainer.Setting
                    title="Provider Sign in Url"
                    description="Configure your SSO provider to use this URL for sign in."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isVisible={properties.isSingleSignOnEnabled.value}
                    isLoading={isLoading}
                >
                    <CodeBlock
                        isCopyable
                        size="sm"
                        code={data?.portal?.singleSignOn?.providerLoginUrl as string}
                    />

                </SettingsContainer.Setting>

                <SettingsContainer.Setting
                    title="Identity Provider Metadata"
                    description={hasIdentityProvider
                        ? "You currently have an identity metadata file uploaded. To upload a "
                        + "different file, click below to remove the current identity file, "
                        + "then select a new file, and save."
                        : " What URL to redirect the user to request authentication"}
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isVisible={properties.isSingleSignOnEnabled.value}
                    isLoading={isLoading}
                >
                    {hasIdentityProvider ? (
                        <Button
                            type="button"
                            variant="danger"
                            size="medium"
                            onClick={() => properties.ssoIdentityProviderMetadata.setValue("")}
                        >
                            Remove Identity File
                        </Button>
                    ) : (
                        <FormGroup
                            type="text"
                            display="textarea"
                            placeholder={"<?xml version=\"1.0\"?>\n<EntityDescriptor.."}
                            isRequired
                            className={Styles["portal-certificate-box"]}
                            onChange={properties.ssoIdentityProviderMetadata.setValueFromInput}
                            value={properties.ssoIdentityProviderMetadata.value}
                        />
                    )}

                </SettingsContainer.Setting>

            </SettingsContainer.Section>

            <SettingsContainer.Section
                title="User Provisioning (SCIM)"
                helpText=" User Provisioning allows you to setup up groups in your Identity Provider which
                    synchronize into Foqal Agent to automatically provision users as Guests, Agents, or
                    Administrators."
            >
                <SettingsContainer.Setting
                    title="Administrator Group"
                    description="Any users that is added to this group will be
                                automaticaly provisioned as an Administrator.
                                All other users provisoned to other groups will be provisioned as agents."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <UserGroups
                        value={properties.scimAdministratorGroupId.value}
                        onSelected={properties.scimAdministratorGroupId.setValue}
                    />
                </SettingsContainer.Setting>

                <SettingsContainer.Setting
                    title="SCIM URL"
                    description="Use this to configure your SCIM server url."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <CodeBlock
                        isCopyable
                        size="sm"
                        code={data?.portal?.userProvisioning?.providerSCIMUrl as string}
                    />
                </SettingsContainer.Setting>
                <SettingsContainer.Setting
                    title="SCIM Token"
                    description="Use this token when asked for the SCIM authentication token."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <CreateToken
                        name="SCIM Token"
                        permissions={[
                            Permission.GroupsRead, Permission.GroupsWrite,
                            Permission.UsersRead, Permission.UsersWrite,
                        ]}
                    />
                </SettingsContainer.Setting>
            </SettingsContainer.Section>

            {entitlementIds.has(entitlementIds.keys.ADVANCED_LOGIN) && (
                <SettingsContainer.Section
                    title="Advanced Login Settings"
                    helpText="Control detailed security and login settings for your portal"
                >
                    <SettingsContainer.Setting
                        title="Allowed Login Types"
                        description="The types of login methods that are allowed by users."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={isLoading}
                    >
                        <Select
                            className="text-gray"
                            placeholder=""
                            isMulti
                            onChange={options => properties.allowedSigninMethods.setValue(
                                options.map(row => row.value as AllowedSignInMethods),
                            )}
                            options={loginTypes}
                            value={loginTypes.filter(option => allowedSigninMethodsSet.has(option.value))}
                        />
                    </SettingsContainer.Setting>

                    <SettingsContainer.Setting
                        title="Default Sign In Method"
                        description="The types of login methods that are allowed by users."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={isLoading}
                    >
                        <Select
                            className="text-gray"
                            placeholder=""
                            isClearable
                            onChange={value => properties.defaultSignInMethod.setValue(value?.value as AllowedSignInMethods)}
                            options={loginTypes}
                            value={loginTypes.find(option => properties.defaultSignInMethod.value === option.value)}
                        />
                    </SettingsContainer.Setting>

                    <SettingsContainer.Setting
                        title="Session Duration"
                        description="The number of hours a signon session can be enabled before another signin is."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={isLoading}
                    >
                        <FormGroup
                            type="number"
                            placeholder="2000"
                            required
                            size="small"
                            onChange={evt => properties.sessionDuration.setValue(
                                parseInt(evt.target.value ?? "0", 10) * Time.HOUR,
                            )}
                            value={(properties.sessionDuration.value ?? 0) / Time.HOUR}
                        />
                    </SettingsContainer.Setting>
                </SettingsContainer.Section>
            )}
            {createLinkElement}
        </SettingsContainer>
    );
}
